// src/hooks/useXeroCashflow.ts
// ─────────────────────────────────────────────────────────────────────────────
// Wraps /api/xero/cashflow-cycle. Returns AR/AP buckets, DSO/DPO and the
// working capital cycle. Mirrors useXeroData's shape: { loading, error, data,
// tenantName } — adviser-facing screens pattern-match on all four.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';

export interface AgedBucketContact {
  name: string;
  current: number;
  days30: number;
  days60: number;
  days90plus: number;
  total: number;
}

export interface AgedBuckets {
  total: number;
  current: number;
  days30: number;
  days60: number;
  days90plus: number;
  contacts: AgedBucketContact[];
}

export interface XeroCashflow {
  debtors: AgedBuckets;
  creditors: AgedBuckets;
  dso: number;
  dpo: number;
  workingCapitalDays: number;
}

export interface XeroCashflowResult {
  loading: boolean;
  data: XeroCashflow | null;
  error: string | null;
  tenantName: string | null;
}

export function useXeroCashflow(tenantId: string | undefined): XeroCashflowResult {
  const [result, setResult] = useState<XeroCashflowResult>({
    loading: true,
    data: null,
    error: null,
    tenantName: null,
  });

  useEffect(() => {
    if (!tenantId) {
      setResult({ loading: false, data: null, error: 'No Xero tenant bound to this client', tenantName: null });
      return;
    }
    const q = `?tenantId=${encodeURIComponent(tenantId)}`;
    Promise.all([
      fetch(`/api/xero/status${q}`).then((r) => r.json()),
      fetch(`/api/xero/cashflow-cycle${q}`).then((r) => r.json()),
    ])
      .then(([status, cashflow]) => {
        if (!status.connected) {
          setResult({ loading: false, data: null, error: 'Xero not connected', tenantName: null });
          return;
        }
        if (cashflow.error) {
          setResult({ loading: false, data: null, error: cashflow.error, tenantName: status.tenantName });
          return;
        }
        setResult({ loading: false, data: cashflow as XeroCashflow, error: null, tenantName: status.tenantName });
      })
      .catch((err) => {
        setResult({ loading: false, data: null, error: err.message, tenantName: null });
      });
  }, [tenantId]);

  return result;
}
